import React, { useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { MdClose } from "react-icons/md";
import {
  notificationModalOpenState,
  notificationListState,
} from "../recoil/notification";
import { useNotification } from "../hooks/useNotification";
import "./NotificationModal.css";

const NotificationModal = () => {
  const [isOpen, setIsOpen] = useRecoilState(notificationModalOpenState);
  const notificationList = useRecoilValue(notificationListState);
  const { fetchNotifications, markAsRead } = useNotification();
  const [currentPage, setCurrentPage] = useState(1);

  const handleNotificationPopupToggle = () => {
    setIsOpen(false);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleNotificationClick = async (notification) => {
    if (notification.is_read) {
      return; // 이미 읽은 알림은 클릭 불가
    }

    await markAsRead(notification.wn_id);
  };

  const handleDeleteNotification = async (wn_id) => {
    // 삭제 기능은 API가 준비되면 구현
    console.log("삭제할 알림 ID:", wn_id);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchNotifications(page);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? "오후" : "오전";
    const displayHours = hours % 12 || 12;

    return `${month}. ${day}. ${ampm} ${String(displayHours).padStart(
      2,
      "0"
    )}:${String(minutes).padStart(2, "0")}`;
  };

  const totalPages = Math.ceil(
    notificationList.total / notificationList.pageSize
  );

  if (!isOpen) return null;

  return (
    <div
      className="notification-popup-overlay"
      onClick={handleNotificationPopupToggle}
    >
      <div className="notification-popup" onClick={(e) => e.stopPropagation()}>
        <div className="notification-popup-header">
          <h3>알림 내역</h3>
          <button
            className="notification-popup-close"
            onClick={handleNotificationPopupToggle}
          >
            <MdClose />
          </button>
        </div>
        <div className="notification-popup-content">
          {notificationList.loading ? (
            <div className="notification-loading">
              <div className="notification-loading-spinner"></div>
              <span>로딩 중...</span>
            </div>
          ) : notificationList.list.length === 0 ? (
            <div className="notification-empty">
              <div className="notification-empty-icon">🔔</div>
              <span>새로운 알림이 없습니다.</span>
            </div>
          ) : (
            <>
              <div className="notification-table">
                <table>
                  <thead>
                    <tr>
                      <th>날짜</th>
                      <th>제목</th>
                      <th>내용</th>
                    </tr>
                  </thead>
                  <tbody>
                    {notificationList.list.map((notification) => (
                      <tr
                        key={notification._id}
                        className={
                          !notification.is_read ? "notification-unread" : ""
                        }
                        onClick={() => {
                          if (!notification.is_read) {
                            handleNotificationClick(notification);
                          }
                        }}
                        style={{
                          cursor: !notification.is_read ? "pointer" : "default",
                        }}
                      >
                        <td className="notification-date-cell">
                          {formatDate(notification.date)}
                        </td>
                        <td className="notification-title-cell">
                          <div className="notification-title-wrapper">
                            <span className="notification-title">
                              {notification.title}
                            </span>
                            {!notification.is_read && (
                              <span className="notification-unread-dot"></span>
                            )}
                          </div>
                        </td>
                        <td className="notification-content-cell">
                          <div className="notification-content-text">
                            {notification.content}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {/* 빈 행 추가 (항상 8개 행 표시) */}
                    {Array.from(
                      { length: Math.max(0, 8 - notificationList.list.length) },
                      (_, index) => (
                        <tr
                          key={`empty-${index}`}
                          className="notification-empty-row"
                        >
                          <td colSpan="3">-</td>
                        </tr>
                      )
                    )}
                  </tbody>
                </table>
              </div>

              {/* 페이징 컨트롤 */}
              <div className="notification-pagination">
                <div className="notification-pagination-info">
                  총 {notificationList.total}개 알림
                </div>
                <div className="notification-pagination-controls">
                  <button
                    className="notification-pagination-btn"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage <= 1}
                  >
                    &lt;
                  </button>
                  {Array.from(
                    {
                      length: Math.ceil(
                        notificationList.total / notificationList.pageSize
                      ),
                    },
                    (_, i) => i + 1
                  ).map((pageNum) => (
                    <button
                      key={pageNum}
                      className={`notification-pagination-btn ${
                        pageNum === currentPage ? "active" : ""
                      }`}
                      onClick={() => handlePageChange(pageNum)}
                    >
                      {pageNum}
                    </button>
                  ))}
                  <button
                    className="notification-pagination-btn"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={
                      currentPage >=
                      Math.ceil(
                        notificationList.total / notificationList.pageSize
                      )
                    }
                  >
                    &gt;
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationModal;
